import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, FileText, Eye, Ban, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useJournalEntries } from '@/hooks/queries/useJournalEntries';
import { useState } from 'react';
import type { JournalType, EntryStatus } from '@/types/accounting';

export const Route = createFileRoute('/apps/accounting/entries/')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: JournalEntriesPage,
});

const JOURNAL_TYPES: { value: JournalType; label: string; labelAr: string; color: string }[] = [
  { value: 'general', label: 'Général', labelAr: 'عام', color: 'gray' },
  { value: 'sales', label: 'Ventes', labelAr: 'المبيعات', color: 'green' },
  { value: 'purchases', label: 'Achats', labelAr: 'المشتريات', color: 'orange' },
  { value: 'bank', label: 'Banque', labelAr: 'البنك', color: 'blue' },
  { value: 'cash', label: 'Caisse', labelAr: 'الصندوق', color: 'purple' },
  { value: 'payroll', label: 'Paie', labelAr: 'الأجور', color: 'pink' },
  { value: 'operations', label: 'Opérations diverses', labelAr: 'عمليات متنوعة', color: 'cyan' },
];

const STATUS_INFO: Record<EntryStatus, { label: string; labelAr: string; color: string }> = {
  draft: { label: 'Brouillon', labelAr: 'مسودة', color: 'yellow' },
  posted: { label: 'Comptabilisé', labelAr: 'محاسب', color: 'green' },
  cancelled: { label: 'Annulé', labelAr: 'ملغى', color: 'red' },
};

function JournalEntriesPage() {
  const { company } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [journalTypeFilter, setJournalTypeFilter] = useState<JournalType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<EntryStatus | 'all'>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data: entries = [], isLoading } = useJournalEntries(company?.id || '', {
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    journalType: journalTypeFilter !== 'all' ? journalTypeFilter : undefined,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    search: searchQuery || undefined,
  });

  // Summary stats
  const stats = {
    total: entries.length,
    posted: entries.filter((e) => e.status === 'posted').length,
    draft: entries.filter((e) => e.status === 'draft').length,
    totalDebit: entries
      .filter((e) => e.status === 'posted')
      .reduce((sum, e) => sum + e.total_debit, 0),
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">قيود اليومية / Écritures comptables</h1>
            <p className="text-muted-foreground mt-2">
              Gestion des écritures comptables / إدارة القيود المحاسبية
            </p>
          </div>
          <Link to="/apps/accounting/entries/new">
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-600">
              <Plus className="w-4 h-4 mr-2" />
              قيد جديد / Nouvelle écriture
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-cyan-600">
            <CardContent className="p-6">
              <div className="text-white">
                <p className="text-sm opacity-90">Total / الإجمالي</p>
                <p className="text-3xl font-bold mt-1">{stats.total}</p>
                <p className="text-xs mt-2 opacity-75">écritures</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-emerald-600">
            <CardContent className="p-6">
              <div className="text-white">
                <p className="text-sm opacity-90">Comptabilisé / محاسب</p>
                <p className="text-3xl font-bold mt-1">{stats.posted}</p>
                <p className="text-xs mt-2 opacity-75">écritures validées</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-yellow-500 to-orange-600">
            <CardContent className="p-6">
              <div className="text-white">
                <p className="text-sm opacity-90">Brouillon / مسودة</p>
                <p className="text-3xl font-bold mt-1">{stats.draft}</p>
                <p className="text-xs mt-2 opacity-75">écritures en attente</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-indigo-600">
            <CardContent className="p-6">
              <div className="text-white">
                <p className="text-sm opacity-90">Total débit / إجمالي المدين</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(stats.totalDebit)}</p>
                <p className="text-xs mt-2 opacity-75">écritures validées</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtres / الفلاتر</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Input
                placeholder="Rechercher (numéro, description)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="md:col-span-2"
              />

              <Select value={journalTypeFilter} onValueChange={(v) => setJournalTypeFilter(v as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Type de journal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {JOURNAL_TYPES.map((jt) => (
                    <SelectItem key={jt.value} value={jt.value}>
                      {jt.label} / {jt.labelAr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                <SelectTrigger>
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="draft">Brouillon / مسودة</SelectItem>
                  <SelectItem value="posted">Comptabilisé / محاسب</SelectItem>
                  <SelectItem value="cancelled">Annulé / ملغى</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  placeholder="Du"
                />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  placeholder="Au"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Entries List */}
        <Card>
          <CardHeader>
            <CardTitle>Écritures comptables / القيود المحاسبية</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">Aucune écriture trouvée</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Commencez par créer votre première écriture comptable
                </p>
                <Link to="/apps/accounting/entries/new">
                  <Button className="mt-4">
                    <Plus className="w-4 h-4 mr-2" />
                    Créer une écriture
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {entries.map((entry) => {
                  const journalType = JOURNAL_TYPES.find((jt) => jt.value === entry.journal_type);
                  const statusInfo = STATUS_INFO[entry.status];

                  return (
                    <Card key={entry.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <p className="font-mono font-bold text-lg">{entry.entry_number}</p>
                              <Badge
                                variant="outline"
                                className={`bg-${journalType?.color}-100 text-${journalType?.color}-700 border-${journalType?.color}-300`}
                              >
                                {journalType?.label}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={`bg-${statusInfo.color}-100 text-${statusInfo.color}-700 border-${statusInfo.color}-300`}
                              >
                                {statusInfo.label}
                              </Badge>
                              {entry.reference_type && (
                                <Badge variant="secondary">{entry.reference_type}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">
                              {entry.description || 'Sans description'}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>Date: {formatDate(entry.entry_date)}</span>
                              <span>•</span>
                              <span>Lignes: {entry.lines?.length || 0}</span>
                              <span>•</span>
                              <span className="font-medium">
                                Débit: {formatCurrency(entry.total_debit)}
                              </span>
                              <span>•</span>
                              <span className="font-medium">
                                Crédit: {formatCurrency(entry.total_credit)}
                              </span>
                              {!entry.is_balanced && (
                                <>
                                  <span>•</span>
                                  <span className="text-red-600 font-medium">⚠ Non équilibré</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Link to={`/apps/accounting/entries/${entry.id}`}>
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4 mr-2" />
                                Voir
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
